import { ChefHat, Plus, AlertCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { RecipeQuickStats } from './components/RecipeQuickStats';
import { RecipeFilters } from './components/RecipeFilters';
import { RecipeManagerTable } from './components/RecipeManagerTable';
import { RecipeScalerDialog } from './components/RecipeScalerDialog';
import { ProgressiveSkeleton, PROGRESSIVE_SKELETONS } from '../../components/ui/progressive-skeleton';

interface RecipeManagerPageViewProps {
  // Data
  recipes: any[];
  stats: {
    totalRecipes: number;
    customRecipes: number;
    scaledToday: number;
    avgScaleFactor: number;
  };
  isLoading: boolean;
  error: any;
  
  // Filters
  searchQuery: string;
  cuisineFilter: string;
  difficultyFilter: string;
  viewMode: 'table' | 'grid';
  availableCuisines: string[];
  
  // Selection
  selectedRecipes: string[];
  
  // Scaler dialog
  scalerDialogOpen: boolean;
  recipeToScale: any;
  onRecipeSaved: () => void;
  
  // Event handlers - Filters
  onSearchChange: (query: string) => void;
  onCuisineChange: (cuisine: string) => void;
  onDifficultyChange: (difficulty: string) => void;
  onViewModeChange: (mode: 'table' | 'grid') => void;
  onClearFilters: () => void;
  
  // Event handlers - Selection
  onSelectRecipe: (recipeId: string, selected: boolean) => void;
  onSelectAll: (selected: boolean) => void;
  
  // Event handlers - Recipe actions
  onScale: (recipe: any) => void;
  onDuplicate: (recipeId: string) => void;
  onDelete: (recipeId: string) => void;
  onView: (recipeId: string) => void;
  onCreateRecipe: () => void;
  
  // Event handlers - Scaler dialog
  onCloseScaler: () => void;
}

/**
 * Recipe Manager Page View
 * 
 * Pure presentation component for the recipe manager interface.
 * Professional chef-focused design with table view, stats, and advanced scaling.
 */
export default function RecipeManagerPageView({
  recipes,
  stats,
  isLoading,
  error,
  searchQuery,
  cuisineFilter,
  difficultyFilter,
  viewMode,
  availableCuisines,
  selectedRecipes,
  scalerDialogOpen,
  recipeToScale,
  onRecipeSaved,
  onSearchChange,
  onCuisineChange,
  onDifficultyChange,
  onViewModeChange,
  onClearFilters,
  onSelectRecipe,
  onSelectAll,
  onScale,
  onDuplicate,
  onDelete,
  onView,
  onCreateRecipe,
  onCloseScaler,
}: RecipeManagerPageViewProps) {
  // Error state
  if (error) {
    return (
      <div className="container mx-auto py-8">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Failed to load recipes. Please try refreshing the page.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="flex items-center gap-3 text-3xl font-bold text-martinique">
            <ChefHat className="h-8 w-8 text-coral" />
            Recipe Manager
          </h1>
          <p className="mt-2 text-muted-foreground">
            Professional recipe scaling and management
          </p>
        </div>
        <Button
          onClick={onCreateRecipe}
          className="bg-coral hover:bg-coral/90 text-white"
        >
          <Plus className="mr-2 h-4 w-4" />
          Create Recipe
        </Button>
      </div>

      {/* Quick Stats */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-24 bg-cream rounded-lg animate-pulse" />
          ))}
        </div>
      ) : (
        <RecipeQuickStats stats={stats} />
      )}

      {/* Filters */}
      <RecipeFilters
        searchQuery={searchQuery}
        onSearchChange={onSearchChange}
        cuisineFilter={cuisineFilter}
        onCuisineChange={onCuisineChange}
        difficultyFilter={difficultyFilter}
        onDifficultyChange={onDifficultyChange}
        viewMode={viewMode}
        onViewModeChange={onViewModeChange}
        availableCuisines={availableCuisines}
        onClearFilters={onClearFilters}
      />

      {/* Selected recipes banner */}
      {selectedRecipes.length > 0 && (
        <div className="bg-coral/10 border border-coral/20 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-coral-800">
              {selectedRecipes.length} recipe{selectedRecipes.length === 1 ? '' : 's'} selected
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onSelectAll(false)}
              >
                Clear Selection
              </Button>
              {/* TODO: Add bulk actions in Phase 2 */}
            </div>
          </div>
        </div>
      )}

      {/* Recipe Table */}
      {isLoading ? (
        <div className="space-y-4">
          <ProgressiveSkeleton
            skeletons={PROGRESSIVE_SKELETONS.recipeGrid(8)}
            duration={1500}
            className="space-y-4"
          />
        </div>
      ) : (
        <RecipeManagerTable
          recipes={recipes}
          selectedRecipes={selectedRecipes}
          onSelectRecipe={onSelectRecipe}
          onSelectAll={onSelectAll}
          onScale={onScale}
          onDuplicate={onDuplicate}
          onDelete={onDelete}
          onView={onView}
        />
      )}

      {/* Recipe Scaler Dialog */}
      <RecipeScalerDialog
        recipe={recipeToScale}
        open={scalerDialogOpen}
        onClose={onCloseScaler}
        onRecipeSaved={onRecipeSaved}
      />
    </div>
  );
}
